import { ReactNode, useState, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import styles from "./ScratchNote.module.css";
import ErrorIcon from "../../../../../setup/images/error-page-image.svg?react";
import { CoreButton, CoreSpinner } from "@verity/components/core";
import i18n from "../../../../../i18n.js";

interface ScratchNoteProps {
	/**
	 * Array of image URLs from S3 with presigned links
	 */
	images:
		| {
				id: string;
				url: string;
				uploadedAt?: number;
		  }[]
		| undefined;
	/**
	 * Optional render prop for when an image fails to load
	 */
	renderOnImageError?: () => ReactNode;
}

/**
 * Default implementation for onImageError
 */
export const defaultRenderOnImageError = (title?: string, description?: string) => (
	<div className={styles.errorContainer}>
		<ErrorIcon className={styles.errorImage} />
		<p className={styles.errorText}>
			{title ?? i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.scratchNote.errorTitle")}
		</p>
		<p className={styles.errorText}>
			{description ??
				i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.scratchNote.errorDescription")}
		</p>
	</div>
);

const defaultRenderNoImage = () => (
	<div className={styles.errorContainer}>
		<ErrorIcon className={styles.errorImage} />
		<p className={styles.errorText}>
			{i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.scratchNote.unavailableTitle")}
		</p>
		<p className={styles.errorText}>
			{i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.scratchNote.unavailableDescription")}
		</p>
	</div>
);

/**
 * ScratchNote component that displays uploaded images from S3 presigned links
 * with pagination for viewing one by one
 */
const ScratchNote = ({ images, renderOnImageError = defaultRenderOnImageError }: ScratchNoteProps) => {
	const [currentImageIndex, setCurrentImageIndex] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);

	// Handle image loading completion
	const handleImageLoad = useCallback(() => {
		setIsLoading(false);
	}, []);

	// Handle image loading error
	const handleImageError = useCallback(() => {
		setIsLoading(false);
		setHasError(true);
	}, []);

	// Navigation controls for pagination
	const goToPreviousImage = useCallback(() => {
		setCurrentImageIndex((prevIndex) => {
			const newIndex = prevIndex - 1;
			// Reset state for the new image
			setIsLoading(true);
			setHasError(false);
			return newIndex;
		});
	}, []);

	const goToNextImage = useCallback(() => {
		setCurrentImageIndex((prevIndex) => {
			const newIndex = prevIndex + 1;
			// Reset state for the new image
			setIsLoading(true);
			setHasError(false);
			return newIndex;
		});
	}, []);

	const renderCurrentImage = () => {
		if (hasError) {
			return renderOnImageError();
		}

		if (!images || images.length === 0) {
			return defaultRenderNoImage();
		}

		return (
			<img
				src={images[currentImageIndex].url}
				alt={`Scratch note ${currentImageIndex + 1}`}
				onLoad={handleImageLoad}
				onError={handleImageError}
				className={`${styles.image} ${isLoading ? styles.hidden : styles.visible}`}
			/>
		);
	};

	return (
		<div className={styles.container}>
			<div className={styles.imageContainer}>
				{isLoading && images && images.length > 0 && <CoreSpinner size={100} />}
				{renderCurrentImage()}
			</div>

			{/* Only show navigation controls if there's more than one image */}
			{images && images.length > 1 && (
				<Box className={styles.navigationControls}>
					<CoreButton
						onClick={goToPreviousImage}
						disabled={currentImageIndex === 0}
						checked
						className={styles.navigationButton}
					>
						{i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.scratchNote.previous")}
					</CoreButton>

					<div className={styles.pagination}>
						<Typography variant="body2" color="inherit">
							{currentImageIndex + 1} / {images.length}
						</Typography>
					</div>

					<CoreButton
						onClick={goToNextImage}
						disabled={currentImageIndex === images.length - 1}
						checked
						className={styles.navigationButton}
					>
						{i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.scratchNote.next")}
					</CoreButton>
				</Box>
			)}
		</div>
	);
};

export default ScratchNote;
